import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import apiService from '@/services/api';
import { useConversation } from '@/contexts/ConversationContext';
import { useDatabaseConnection } from '@/contexts/DatabaseConnectionContext';
import { useQuerySteps } from '@/contexts/AppStateContext';
import { ResultCanvas } from '@/components/QueryGenerator/ResultCanvas';
import { QueryPanel, QueryStep } from '@/components/QueryGenerator/QueryPanel';

export const ConversationInterface: React.FC = () => {
  const {
    currentConversation,
    messages,
    isLoading: isProcessing,
    selectedConnectionId,
    processQuery,
  } = useConversation();

  // Use centralized database connection state
  const {
    connections,
    loadConnections: loadDatabaseConnections
  } = useDatabaseConnection();

  // Use query state management
  const { steps, queryState: currentStep } = useQuerySteps();

  // Local component state
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryStep, setQueryStep] = useState<QueryStep>('idle');

  // Get selected connection name
  const selectedConnection = connections.find(c => c.id === selectedConnectionId);
  const databaseName = selectedConnection?.alias || 'Select Database';

  // Load connections on mount
  useEffect(() => {
    loadDatabaseConnections();
  }, []);

  // Get the latest SQL message for display in ResultCanvas
  const latestSQLMessage = useMemo(() => {
    const sqlMessages = messages.filter(m => m.message_type === 'SQL');
    return sqlMessages.length > 0 ? sqlMessages[sqlMessages.length - 1] : null;
  }, [messages]);

  // Get the latest user query for display
  const latestUserQuery = useMemo(() => {
    const userMessages = messages.filter(m => m.role === 'USER');
    return userMessages.length > 0 ? userMessages[userMessages.length - 1].content : '';
  }, [messages]);

  // Update currentQuery when conversation loads
  useEffect(() => {
    if (latestUserQuery && !currentQuery) {
      setCurrentQuery(latestUserQuery);
    }
  }, [latestUserQuery]);

  // Reset state when conversation changes
  useEffect(() => {
    setCurrentQuery(latestUserQuery || '');
    setQueryStep(latestSQLMessage ? 'complete' : 'idle');
  }, [currentConversation?.id]);

  // Map query steps to QueryStep type
  useEffect(() => {
    switch (currentStep) {
      case 'idle':
        setQueryStep('idle');
        break;
      case 'analyzing':
        setQueryStep('scanning');
        break;
      case 'generating':
        setQueryStep('building');
        break;
      case 'executing':
        setQueryStep('running');
        break;
      case 'completed':
        setQueryStep('complete');
        break;
      case 'error':
        setQueryStep('error');
        break;
      default:
        setQueryStep('idle');
    }
  }, [currentStep]);

  const handleSendQuery = async (query: string) => {
    if (!query.trim() || isProcessing) return;
    if (!currentConversation) {
      toast.error('Please create a conversation first');
      return;
    }

    setCurrentQuery(query);
    setQueryStep('scanning');
    steps.analyze();

    try {
      steps.generate();
      setQueryStep('building');
      await processQuery(query);
      setQueryStep('running');
      steps.complete();
      setTimeout(() => setQueryStep('complete'), 500);
    } catch (error) {
      setQueryStep('error');
      toast.error('Failed to generate SQL');
      setTimeout(() => setQueryStep('idle'), 3000);
    }
  };

  const handleExportResults = async (sqlGenerationId: string) => {
    try {
      const blob = await apiService.exportCSV(sqlGenerationId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `query-results-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="flex h-full w-full">
      {/* Center Panel - Result Canvas */}
      <ResultCanvas
        currentMessage={latestSQLMessage}
        onExportResults={handleExportResults}
        className="flex-1"
      />

      {/* Right Panel - Query Panel */}
      <QueryPanel
        databaseName={databaseName}
        currentQuery={currentQuery}
        queryStep={queryStep}
        onSendQuery={handleSendQuery}
        isProcessing={isProcessing}
        selectedModel={selectedModel}
        onModelChange={setSelectedModel}
      />
    </div>
  );
};

export default ConversationInterface;